import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './styles.css';
import {
  Autocomplete,
  Avatar,
  Box,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { colors } from '../../common/pallette/colors';
import { setCurrentClub } from '../../redux/reducers/clubDetails';
import { resetActionPlan } from '../../redux/reducers/actionPlan';
import { resetClubData } from '../../redux/reducers/clubData';
import { resetDataCaptures } from '../../redux/reducers/dataCaptures';
import { resetEvaluation } from '../../redux/reducers/evaluation';
import { resetGoalsAndStrategy } from '../../redux/reducers/goalsAndStrategy';
import { resetLibraryData } from '../../redux/reducers/library';

type ClubOption = {
  id: number;
  name: string;
  color?: string;
  imageUrl?: string;
  logo?: string;
  currentEvaluationPhase?: any;
  textColor?: string;
};

export default function SearchDropBox(props: {
  placeholder?: string;
  selectedValue?: (club?: ClubOption) => void;
  autoFocus?: boolean;
}) {
  const dispatch: any = useDispatch();
  const { placeholder, selectedValue, autoFocus } = props;
  const clubDetails = useSelector((state: any) => state.common.myClubs);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocus) {
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [autoFocus]);

  const options: ClubOption[] = Array.isArray(clubDetails) ? clubDetails : [];

  const manageSelect = (club: ClubOption | null) => {
    if (!club) return;
    const d = {
      id: club.id,
      name: club.name,
      color: club.color,
      logo: club.imageUrl || club.logo,
      currentEvaluationPhase: club.currentEvaluationPhase,
      textColor: club?.textColor,
    };
    selectedValue?.(d);
    dispatch((setCurrentClub as any)(d));
    dispatch(resetActionPlan());
    dispatch(resetGoalsAndStrategy());
    dispatch(resetClubData());
    dispatch(resetDataCaptures());
    dispatch(resetEvaluation());
    dispatch(resetLibraryData());
  };

  const logoSrc = (club: ClubOption) => {
    const path = club.imageUrl || club.logo;
    if (!path) return undefined;
    if (path.startsWith('http')) return path;
    return `https://jamp.football${path}`;
  };

  return (
    <Box className="clubSwitchSearch">
      <Autocomplete
        options={options}
        getOptionLabel={(option) => option?.name ?? ''}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        filterOptions={(opts, state) => {
          const q = state.inputValue.trim().toLowerCase();
          if (!q) return opts;
          return opts.filter((o) => o.name?.toLowerCase().includes(q));
        }}
        inputValue={inputValue}
        onInputChange={(_, v) => setInputValue(v)}
        onChange={(_, value) => manageSelect(value)}
        noOptionsText={
          <Typography sx={{ fontSize: '12px', color: colors.grey.dark, py: 0.5 }}>
            No clubs match
          </Typography>
        }
        slotProps={{
          paper: {
            className: 'clubSwitchSearch__paper',
            elevation: 0,
          },
          listbox: {
            style: { maxHeight: 280 },
          },
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.id}
            className="clubSwitchSearch__option"
          >
            <Avatar
              src={logoSrc(option)}
              alt=""
              sx={{
                width: 28,
                height: 28,
                bgcolor: option.color || colors.grey.light,
                fontSize: '11px',
                flexShrink: 0,
              }}
            >
              {option.name?.[0]}
            </Avatar>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: colors.black.dark,
                fontFamily: 'Noto Sans',
                lineHeight: 1.3,
              }}
            >
              {option.name}
            </Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder={placeholder || 'Search clubs…'}
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ width: 18, height: 18, color: colors.grey.dark }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                fontSize: '13px',
                fontFamily: 'Noto Sans',
                backgroundColor: colors.grey.bright,
                pr: '8px !important',
                '& fieldset': { border: `1px solid ${colors.grey.light}` },
                '&:hover fieldset': { border: `1px solid ${colors.grey.dark}` },
                '&.Mui-focused fieldset': { border: `1px solid ${colors.blue.navy}` },
              },
              '& input': {
                padding: '8px 4px !important',
                color: colors.black.dark,
              },
            }}
          />
        )}
      />
    </Box>
  );
}
